import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import Swal from 'sweetalert2';
import PageBuilder from '@/components/PageBuilder/PageBuilder.vue';
import { DEFAULT_PAGE_CONFIG } from '@/components/PageBuilder/page_builder.js';

export default {
  name: 'ThankYouConfiguration',
  components: {
    PageBuilder
  },
  data() {
    return {
      configuration: { ...DEFAULT_PAGE_CONFIG },
      original_configuration: null,
      is_saving: false,
      is_loading: true,
      show_json_preview: false
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    has_changes() {
      if (!this.original_configuration) return false;
      return JSON.stringify(this.configuration) !== JSON.stringify(this.original_configuration);
    },
    json_preview() {
      return JSON.stringify(this.configuration, null, 2);
    }
  },
  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    this.load_configuration();
  },
  methods: {
    // Export configuration as JSON file
    export_json() {
      const json_content = JSON.stringify(this.configuration, null, 2);
      const blob = new Blob([json_content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `thank_you_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Exported',
        text: 'Configuration exported as JSON file.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    },

    // Import configuration from JSON file
    import_json() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const imported_config = JSON.parse(e.target.result);
            
            const valid_keys = Object.keys(DEFAULT_PAGE_CONFIG);
            const imported_keys = Object.keys(imported_config);
            const is_valid = imported_keys.some(key => valid_keys.includes(key));

            if (!is_valid) {
              throw new Error('Invalid configuration format');
            }

            this.configuration = { ...DEFAULT_PAGE_CONFIG, ...imported_config };

            Swal.fire({
              icon: 'success',
              title: 'Imported',
              text: 'Configuration imported successfully. Remember to save!',
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false
            });
          } catch (error) {
            console.error('Import error:', error);
            Swal.fire({
              icon: 'error',
              title: 'Import Failed',
              text: 'Invalid JSON file. Please check the file format.',
              confirmButtonText: 'OK'
            });
          }
        };
        reader.readAsText(file);
      };

      input.click();
    },

    copy_json() {
      navigator.clipboard.writeText(this.json_preview).then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Copied',
          text: 'JSON copied to clipboard.',
          timer: 1500,
          timerProgressBar: true,
          showConfirmButton: false
        });
      }).catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to copy to clipboard.',
          timer: 2000,
          showConfirmButton: false
        });
      });
    },

    toggle_json_preview() {
      this.show_json_preview = !this.show_json_preview;
    },

    async load_configuration() {
      const token = localStorage.getItem('token');
      const base_url = API_BASE_URL;

      try {
        let url = `${base_url}/verify_api/workflow_management_api/get_thank_you_configuration/`;
        if (this.workflow_id) {
          url += `?workflow_id=${this.workflow_id}`;
        }
        const response = await fetch(
          url,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.configuration && Object.keys(data.configuration).length > 0) {
            this.configuration = { ...DEFAULT_PAGE_CONFIG, ...data.configuration };
          } else {
            this.configuration = { ...DEFAULT_PAGE_CONFIG };
          }
          this.original_configuration = JSON.parse(JSON.stringify(this.configuration));
        } else {
          this.configuration = { ...DEFAULT_PAGE_CONFIG };
          this.original_configuration = JSON.parse(JSON.stringify(this.configuration));
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        this.original_configuration = JSON.parse(JSON.stringify(this.configuration));
      } finally {
        this.is_loading = false;
      }
    },

    async save_configuration() {
      this.is_saving = true;
      const token = localStorage.getItem('token');
      const base_url = API_BASE_URL;

      try {
        const payload = {
          workflow_id: this.workflow_id,
          configuration: this.configuration
        };

        const response = await fetch(
          `${base_url}/verify_api/workflow_management_api/save_thank_you_configuration/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`
            },
            body: JSON.stringify(payload)
          }
        );

        if (response.ok) {
          Swal.fire({
            icon: 'success',
            title: 'Configuration Saved',
            text: 'Your thank you page configuration has been saved successfully.',
            confirmButtonText: 'OK'
          });
          this.original_configuration = JSON.parse(JSON.stringify(this.configuration));
        } else {
          throw new Error('Failed to save configuration');
        }
      } catch (error) {
        console.error('Failed to save configuration:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save configuration. Please try again.',
          confirmButtonText: 'OK'
        });
      } finally {
        this.is_saving = false;
      }
    },

    reset_to_defaults() {
      Swal.fire({
        title: 'Reset to Defaults?',
        text: 'This will restore all fields to their default values.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reset',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.configuration = JSON.parse(JSON.stringify(DEFAULT_PAGE_CONFIG));
        }
      });
    },

    undo_changes() {
      if (this.original_configuration) {
        this.configuration = JSON.parse(JSON.stringify(this.original_configuration));
      }
    }
  }
};
